/**
 * Helper: Utils
 * ------------------------------------------------------------------------------
 * Frame utility functions.
 *
 * @namespace utils
 */

import 'mdn-polyfills/Node.prototype.prepend'

import cssClasses from './cssClasses'

/**
 * Combine two objects using properties as the override.
 * @param {object} defaults - Defaults options defined in script.
 * @param {object} properties - Options defined by user.
 * @return {object} - Defaults modified options.
 */
export function extendDefaults (defaults, properties) {
    for (const property in properties) {
        if (properties != null && typeof properties !== 'undefined') {
            defaults[property] = properties[property]
        }
    }

    return defaults
}

/**
 * Retrieves input data from a form.
 * @param {HTMLForm} form - HTML form elements.
 * @returns {Object} - Form data as an object literal.
 */
export function formToJSON (elements) {
    return [].reduce.call(elements, (data, element) => {
        if (element.name && element.value) {
            data[element.name] = element.value
        }
        return data
    }, {})
}

/**
 * Serialize all form data into a query string.
 * @param {Node} form - The form to serialize.
 * @return {String} - The serialized form data.
 */
export function serialize (form) {
    const serialized = []

    /**
     * Loop through each field in the form.
     */
    for (let i = 0; i < form.elements.length; i++) {
        const field = form.elements[i]

        // Don't serialize fields without a name, submits, buttons, file and reset inputs, and disabled fields
        if (!field.name || field.disabled || field.type === 'file' || field.type === 'reset' || field.type === 'submit' || field.type === 'button') {
            continue
        }

        /**
         * If a multi-select, get all selections.
         */
        if (field.type === 'select-multiple') {
            for (let counter = 0; counter < field.options.length; counter++) {
                if (!field.options[counter].selected) {
                    continue
                }

                serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.options[counter].value)}`)
            }
        } else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
            serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`)
        }
    }

    return serialized.join('&')
}

/**
 * Shortcut function to add an event listener.
 * (c) 2017 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {String} event -The event type.
 * @param {Node} elem - The element to attach the event to (optional, defaults to window).
 * @param {Function} callback - The callback to run on the event.
 * @param {Boolean} capture - If true, forces bubbling on non-bubbling events.
 */
export function on (event, elem = window, callback, capture = false) {
    /**
     * If only a string is passed into the element parameter.
     */
    if (typeof elem === 'string') {
        document.querySelector(elem).addEventListener(event, callback, capture)
        return
    }

    /**
     * If an element is not defined in parameters, then shift callback across.
     */
    if (typeof elem === 'function') {
        window.addEventListener(event, elem)
        return
    }

    /**
     * Default listener.
     */
    elem.addEventListener(event, callback, capture)
}

/**
 * Get all siblings of an element.
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {Node} elem - The element you want get the siblings of.
 * @return {Array} - The siblings.
 */
export function getSiblings (elem) {
    return Array.prototype.filter.call(elem.parentNode.children, (sibling) => {
        return sibling !== elem
    })
}

/**
 * Render function.
 * @param {String} template - The template string to render.
 * @param {Node} node - The node to render into.
 */
export function render (template, node) {
    if (!node) {
        return
    }

    node.innerHTML = template
}

/**
 * Browser detect and return a string
 * - https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
 * @returns {String} browser string.
 */
export function getBrowser () {
    const userAgent = window.navigator.userAgent

    // The order matters here, and this may report false positives for unlisted browsers.
    if (userAgent.indexOf('Firefox') > -1) {
        // 'Mozilla/5.0 (X11 Ubuntu Linux x86_64 rv:61.0) Gecko/20100101 Firefox/61.0'
        return 'Firefox'
    } else if (userAgent.indexOf('Opera') > -1) {
        return 'Opera'
    } else if (userAgent.indexOf('Trident') > -1) {
        // 'Mozilla/5.0 (Windows NT 10.0 WOW64 Trident/7.0 .NET4.0C .NET4.0E Zoom 3.6.0 wbx 1.0.0 rv:11.0) like Gecko'
        return 'Internet Explorer'
    } else if (userAgent.indexOf('Edge') > -1) {
        // 'Mozilla/5.0 (Windows NT 10.0 Win64 x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299'
        return 'Edge'
    } else if (userAgent.indexOf('Chrome') > -1) {
        // 'Mozilla/5.0 (X11 Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36'
        return 'Chrome'
    } else if (userAgent.indexOf('Safari') > -1) {
        // 'Mozilla/5.0 (iPhone CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306'
        return 'Safari'
    }

    return 'unknown'
}

/**
 * Car image.
 * @param {Object} currentCar - Object containing the model and year.
 */
export function carImage (currentCar) {
    const url = window.theme.url.split('?')
    const filepath = url[0]
    const version = url[1]

    let completeUrl = ''

    if (currentCar.method === 'lookup') {
        completeUrl = `${filepath}Car_VehicleCode_${currentCar.vehicleCode}.png?${version}`
    } else {
        const modelFormatted = currentCar.model.replace(/\+/g, 'PLUS')
        const yearFormatted = currentCar.year.replace(/\//g, '-').replace(/ /g, '')

        completeUrl = `${filepath}Car_Model_${modelFormatted}_${yearFormatted}.png?${version}`
    }

    return completeUrl
}

/**
 * Image URL parameters.
 * @param {String} url - The full image URL.
 * @param {Object} parameters - Object containing size, crop (optional), format (optional) and scale (optional)
 */
export function imageParameters (url, parameters) {
    if (url === null) {
        return ''
    }

    let filePath = url.split('?')

    /**
     * Find out old URLs format and version
     */
    const original = {
        version: filePath[1],
        filetype: (url.indexOf('.jpg') > 0) ? 'jpg' : 'png'
    }

    /**
     * Use regex to test to see if the URL already has a size (matching 123x, x123, 123x123, master, original, large, medium, small)
     * If it does remove it
     */
    const formattedUrl = filePath[0].replace(`.${original.filetype}`, '').split('_')
    const urlParameters = formattedUrl[formattedUrl.length - 1]
    const regex = /^(master|original|large|medium|small)|([0-9]+[xX][0-9]+)|([0-9]+[xX])|([xX][0-9]+)$/

    if (regex.test(urlParameters)) {
        filePath = filePath[0].replace(`_${urlParameters}.${original.filetype}`, '')
    } else {
        filePath = filePath[0].replace(`.${original.filetype}`, '')
    }

    /**
     * Set new URL parameters
     */
    const output = {}
    if (parameters.size === 'master') {
        output.size = ''
    } else {
        output.size = `_${parameters.size}`
    }

    output.crop = (parameters.crop) ? `_crop_${parameters.crop}` : ''
    output.scale = (parameters.scale === 2 || parameters.scale === 3) ? `@${parameters.scale}x` : ''
    output.version = (original.version) ? `?${original.version}` : ''

    if (parameters.format === 'pjpg') {
        if (original.filetype === 'png') {
            output.filetype = '.progressive.png.jpg'
        } else {
            output.filetype = '.progressive.jpg'
        }
    } else if (parameters.format === 'jpg') {
        if (original.filetype === 'png') {
            output.filetype = '.png.jpg'
        } else {
            output.filetype = '.jpg'
        }
    } else {
        output.filetype = `.${original.filetype}`
    }

    const completeUrl = `${filePath}${output.size}${output.crop}${output.scale}${output.filetype}${output.version}`

    return completeUrl
}

/**
 * Open target component and fire global open event.
 * @param {String} property Property to sort by
 * @returns {Object} Sorted object
 */
export function sortByProperty (property) {
    return function (x, y) {
        const greaterThan = (x[property] > y[property]) ? 1 : -1
        return (x[property] === y[property]) ? 0 : greaterThan
    }
}

/**
 * Return a comma-formatted number.
 * @param {String} x the number to be formatted.
 * @returns {String} the formatted number.
 */
export function formatNumber (x) {
    let theme = window.theme
    let character = ','
    let joinCharacter = '.'

    if (theme.numberFormat === 'fullstop') {
        character = '.'
        joinCharacter = ','
    } else if (theme.numberFormat === 'space') {
        character = ' '
        joinCharacter = '.'
    }

    const parts = x.toString().split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, character)

    return parts.join(joinCharacter)
}

/**
 * Return formatted product title with HTML .
 * @param {Object} item the full product object.
 * @returns {String} the formatted product title HTML.
 */
export function getProductTitle (item) {
    if (item.product_type === 'ESB') {
        return `<span class="ajax-cart__title h6">${item.product_title}</span>`
    }

    return `<a href="${item.url}" class="ajax-cart__title ajax-cart__title--link h6">${item.product_title}</a>`
}

/**
 * Move element inside container element.
 * @param {Node} item the element to be moved.
 * @param {Node} destination the element destination to move the item.
 * @param {String} method choose to `append` or `prepend` the item.
 * @param {String} addedClass the class added to the destination once item has been moved.
 * @param {Boolean} hide if true then don't add `is-active` class.
 */
export function moveElement (item, destination, method, addedClass, hide) {
    if (destination.classList.contains(addedClass)) {
        return
    }

    const clonedItem = item.cloneNode(true)

    if (method === 'prepend') {
        destination.prepend(clonedItem)
    } else {
        destination.appendChild(clonedItem)
    }

    if (!hide) {
        clonedItem.classList.add(cssClasses.active)
    }

    destination.classList.add(addedClass)
}

/**
 * Calculates percentage saving between the compare at price and current price.
 * @param {Integer} price – Current product price.
 * @param {Integer} compareAtPrice – Compare at price.
 * @param {Bool} isKlevu - Flip values for Klevu search
 */
export function percentageSaving (price, compareAtPrice, isKlevu = false) {
    if (isKlevu) {
        return Math.round(((price - compareAtPrice) * 100) / price)
    } else {
        return Math.round(((compareAtPrice - price) * 100) / compareAtPrice)
    }
}

/**
 * Determine if the link has an external href.
 * @param {Integer} link site link
 */
export function linkExternal (link) {
    if (link.host === '') {
        return false
    }

    return (link.host !== window.location.host)
}

/**
 * Get query parameters as an object.
 * @return {Object} - Query object.
 */
export function getQueryParamsObject () {
    const result = {}
    const parser = document.createElement('a')

    parser.href = window.location.href

    const query = parser.search.substring(1)
    const vars = query.split('&')

    for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=')
        result[pair[0]] = decodeURIComponent(pair[1])
    }

    return result
}

/**
 * Strips the HTML from a given string.
 * @param {String} string - The string to split.
 * @returns {String}
 */
export function stripHtml (string) {
    const element = document.createElement('div')

    element.innerHTML = string
    return element.textContent || element.innerText || ''
}



// WEBPACK FOOTER //
// ./src/resources/scripts/helpers/utils.js